const TWITCH_VOD_ARCHIVE_RE = /^https?:\/\/(?:www\.)?twitch\.tv\/videos\/(\d+)\/?$/i;

function normalizeTwitchVodUrl(url: string): string {
  const stripped = (url || '').trim();
  if (!stripped) {
    return '';
  }
  try {
    const parsed = new URL(stripped);
    let path = parsed.pathname;
    if (path.endsWith('/') && path.length > 1) {
      path = path.slice(0, -1);
    }
    return `${parsed.protocol}//${parsed.host.toLowerCase()}${path}`;
  } catch {
    return stripped;
  }
}

/** Validate a Twitch archive VOD URL (not clips or channel URLs). */
export function validateTwitchVodArchiveUrl(url: string): boolean {
  const normalized = normalizeTwitchVodUrl(url);
  if (!normalized) {
    return false;
  }
  return TWITCH_VOD_ARCHIVE_RE.test(normalized);
}

export function parseOptionalOffsetSeconds(value: string, fieldLabel: string): number | null | string {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return `${fieldLabel} must be a non-negative whole number of seconds.`;
  }
  return parsed;
}

export interface CfbVodIntakeFormValues {
  twitchVodUrl: string;
  startOffsetSec: string;
  endOffsetSec: string;
}

/** Client-side validation for commissioner VOD intake submission. */
export function validateVodIntakeForm(values: CfbVodIntakeFormValues): string | null {
  const url = values.twitchVodUrl.trim();
  if (!url) {
    return 'Paste a Twitch VOD archive URL.';
  }
  if (!validateTwitchVodArchiveUrl(url)) {
    return 'Only Twitch VOD archive URLs are supported (https://www.twitch.tv/videos/...).';
  }

  const start = parseOptionalOffsetSeconds(values.startOffsetSec, 'Start offset');
  if (typeof start === 'string') {
    return start;
  }
  const end = parseOptionalOffsetSeconds(values.endOffsetSec, 'End offset');
  if (typeof end === 'string') {
    return end;
  }
  if (start != null && end != null && end <= start) {
    return 'End offset must be greater than start offset.';
  }
  return null;
}

export function buildVodIntakeRequestBody(values: CfbVodIntakeFormValues): {
  twitch_vod_url: string;
  start_offset_sec?: number;
  end_offset_sec?: number;
} {
  const start = parseOptionalOffsetSeconds(values.startOffsetSec, 'Start offset');
  const end = parseOptionalOffsetSeconds(values.endOffsetSec, 'End offset');
  const body: {
    twitch_vod_url: string;
    start_offset_sec?: number;
    end_offset_sec?: number;
  } = {
    twitch_vod_url: values.twitchVodUrl.trim(),
  };
  if (typeof start === 'number') {
    body.start_offset_sec = start;
  }
  if (typeof end === 'number') {
    body.end_offset_sec = end;
  }
  return body;
}
